import React, { useState, useEffect } from 'react';
import { 
  FiActivity, 
  FiSearch, 
  FiFilter, 
  FiCalendar,
  FiUser,
  FiFileText,
  FiTrash2,
  FiEdit,
  FiPlus,
  FiEye,
  FiDownload,
  FiRefreshCw,
  FiBarChart2,
  FiClock,
  FiTrendingUp,
  FiTrendingDown
} from 'react-icons/fi';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import './Activity.css';

function Activity() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [analytics, setAnalytics] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAnalytics, setShowAnalytics] = useState(false);

  const userId = config.getUserId();

  useEffect(() => {
    if (userId) {
      fetchActivities();
      fetchAnalytics();
    }
  }, [userId, currentPage, searchTerm, actionFilter, dateFilter]);

  const fetchActivities = async () => {
    try {
      if (!userId) {
        console.error('Missing user ID:', { userId });
        setError('User data not available');
        return;
      }

      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        action: actionFilter !== 'all' ? actionFilter : undefined,
        search: searchTerm || undefined,
        startDate: dateFilter === 'today' ? new Date().toISOString().split('T')[0] : undefined,
        endDate: dateFilter === 'today' ? new Date().toISOString().split('T')[0] : undefined
      };

      // Use the correct API route without CompanyId
      const url = `/activities/user/${userId}`;

      const response = await AxiosInstance.get(url, { params });
      
      // Handle both 200 and 304 responses
      if (response.status === 200 || response.status === 304) {
        // Check if response has the expected structure
        if (response.data && response.data.statusCode === 200) {
          setActivities(response.data.data || []);
          setTotalPages(response.data.pagination?.pages || 1);
          setError(null); // Clear any previous errors
        } else if (response.data && Array.isArray(response.data)) {
          // Handle direct array response
          setActivities(response.data);
          setError(null);
        } else if (response.data && response.data.data) {
          // Handle nested data structure
          setActivities(response.data.data || []);
          setTotalPages(response.data.pagination?.pages || 1);
          setError(null);
        } else {
          // Empty response or unexpected structure
          setActivities([]);
          setError(null);
        }
      } else {
        setError('Failed to load activities');
      }
    } catch (err) {
      console.error('Error fetching activities:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to load activities');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      if (!userId) {
        console.error('Missing user ID for analytics:', { userId });
        return;
      }

      const url = `/activities/analytics/${userId}`;

      const response = await AxiosInstance.get(url);
      
      if (response.data.statusCode === 200) {
        setAnalytics(response.data);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      console.error('Analytics error details:', err.response?.data);
    }
  };

  const getActionIcon = (action) => {
    switch (action?.toLowerCase()) {
      case 'create':
      case 'add':
        return <FiPlus className="action-icon create" />;
      case 'update':
      case 'edit':
        return <FiEdit className="action-icon update" />;
      case 'delete':
      case 'remove':
        return <FiTrash2 className="action-icon delete" />;
      case 'login':
        return <FiUser className="action-icon login" />;
      case 'view':
      case 'read':
        return <FiEye className="action-icon view" />;
      case 'download':
        return <FiDownload className="action-icon download" />;
      default:
        return <FiActivity className="action-icon default" />;
    }
  };

  const getActionColor = (action) => {
    switch (action?.toLowerCase()) {
      case 'create':
      case 'add':
        return '#10b981';
      case 'update':
      case 'edit':
        return '#f59e0b';
      case 'delete':
      case 'remove':
        return '#ef4444';
      case 'login':
        return '#06b6d4';
      case 'view':
      case 'read':
        return '#3b82f6';
      case 'download':
        return '#8b5cf6';
      default:
        return '#6b7280';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'action') {
      setActionFilter(value);
    } else if (filterType === 'date') {
      setDateFilter(value);
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (!userId) {
    return (
      <div className="page-main">
        <div className="page-title-row">
          <h1><FiActivity className="page-title-icon" size={28} />{" "}Activity</h1>
        </div>
        <div className="error-message">
          <p>User data not available. Please log in again.</p>
          <button onClick={() => window.location.reload()} className="retry-btn">
            <FiRefreshCw />
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  if (loading && activities.length === 0) {
    return (
      <div className="page-main">
        <div className="page-title-row">
          <h1><FiActivity className="page-title-icon" size={28} />{" "}Activity</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading activities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-main">
      <div className="page-title-row">
        <h1><FiActivity className="page-title-icon" size={28} />{" "}Activity</h1>
        <button 
          className="analytics-toggle-btn"
          onClick={() => setShowAnalytics(!showAnalytics)}
        >
          <FiBarChart2 />
          {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
        </button>
      </div>

      {/* Analytics Section */}
      {showAnalytics && analytics && (
        <div className="analytics-section">
          <div className="analytics-grid">
            <div className="analytics-card">
              <div className="analytics-icon">
                <FiTrendingUp />
              </div>
              <div className="analytics-content">
                <h3>{analytics.totalActivities}</h3>
                <p>Total Activities</p>
              </div>
            </div>
            
            {analytics.actionStats?.map((stat, index) => (
              <div key={index} className="analytics-card">
                <div className="analytics-icon" style={{ color: getActionColor(stat._id) }}>
                  {getActionIcon(stat._id)}
                </div>
                <div className="analytics-content">
                  <h3>{stat.count}</h3>
                  <p>{stat._id} Actions</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="filters-section">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search activities..."
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
        </div>

        <div className="filter-controls">
          <div className="filter-group">
            <FiFilter className="filter-icon" />
            <select
              value={actionFilter}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="filter-select"
            >
              <option value="all">All Actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="login">Login</option>
              <option value="view">View</option>
              <option value="download">Download</option>
              <option value="add">Add</option>
            </select>
          </div>

          <div className="filter-group">
            <FiCalendar className="filter-icon" />
            <select
              value={dateFilter}
              onChange={(e) => handleFilterChange('date', e.target.value)}
              className="filter-select"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>
      </div>

      {/* Activities List */}
      <div className="activities-container">
        {error ? (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={fetchActivities} className="retry-btn">
              <FiRefreshCw />
              Retry
            </button>
          </div>
        ) : activities.length === 0 ? (
          <div className="empty-state">
            <FiActivity className="empty-icon" />
            <h3>No activities found</h3>
            <p>Your recent activities will appear here</p>
          </div>
        ) : (
          <>
            <div className="activities-list">
              {activities.map((activity) => (
                <div key={activity.ActivityId} className="activity-item">
                  <div className="activity-icon" style={{ backgroundColor: getActionColor(activity.Action) + '20' }}>
                    {getActionIcon(activity.Action)}
                  </div>
                  
                  <div className="activity-content">
                    <div className="activity-header">
                      <h4 className="activity-title">{activity.Activity}</h4>
                      <span className="activity-time">
                        <FiClock />
                        {formatTime(activity.createdAt)}
                      </span>
                    </div>
                    
                    <div className="activity-details">
                      <span className="activity-action" style={{ color: getActionColor(activity.Action) }}>
                        {activity.Action}
                      </span>
                      {activity.Entity && (
                        <span className="activity-entity">
                          on {activity.Entity}
                        </span>
                      )}
                      {activity.Reason && (
                        <span className="activity-reason">
                          - {activity.Reason}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                
                <div className="page-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Activity; 